"use client";

import { useState } from "react";
import { useRouter } from "@/i18n/navigation";
import { Link } from "@/i18n/navigation";
import { useTranslations } from "next-intl";
import { Search, ArrowLeft, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Navbar } from "@/components/layout/navbar";

export default function TrackingPage() {
  const t = useTranslations("tracking");
  const router = useRouter();
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    if (!code.trim()) return;
    setLoading(true);
    setError("");

    const res = await fetch(`/api/orders/${encodeURIComponent(code.trim())}`);
    if (res.ok) {
      const data = await res.json();
      router.push(`/order/${data.order.id}`);
    } else {
      setError(t("notFound"));
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-md mx-auto px-4 py-24">
        <Link href="/" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-zinc-300 mb-8">
          <ArrowLeft className="h-4 w-4" />
          {t("title")}
        </Link>
        <div className="text-center mb-10">
          <div className="w-14 h-14 rounded-full bg-(--accent)/10 border border-(--accent)/25 flex items-center justify-center mx-auto mb-4">
            <Package className="h-6 w-6 text-(--accent)" />
          </div>
          <h1 className="text-3xl page-title mb-2">{t("title")}</h1>
          <p className="text-zinc-400 text-sm">{t("subtitle")}</p>
        </div>
        <form onSubmit={handleSearch} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="code">{t("label")}</Label>
            <Input
              id="code"
              placeholder={t("placeholder")}
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              className="text-center text-lg font-mono tracking-wider"
            />
            <p className="text-xs text-zinc-400 text-center">{t("hint")}</p>
          </div>
          {error && (
            <div className="rounded-lg border border-red-800 bg-red-950 p-3 text-sm text-red-300 text-center">{error}</div>
          )}
          <Button type="submit" className="w-full gap-2" disabled={!code.trim() || loading}>
            {loading ? (
              <><span className="h-4 w-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />{t("searching")}</>
            ) : (
              <><Search className="h-4 w-4" />{t("button")}</>
            )}
          </Button>
        </form>
        <div className="mt-10 p-4 rounded-lg border border-white/10 bg-white/[0.04]">
          <p className="text-xs text-zinc-400 text-center mb-2">{t("demo")}</p>
          <div className="flex flex-col gap-2">
            {["SP-2024-001", "SP-2024-002"].map((c) => (
              <button key={c} onClick={() => setCode(c)} className="text-sm font-mono text-(--accent) hover:text-white text-center py-1">{c}</button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
